<template lang="pug">
.benefit-detail(v-if="!loading", v-loading="saving")
  el-header
    el-col(:span="16")
      el-page-header(@back="back", :content="isNew ? 'Novo benefício' : `${benefit.title}`")

    el-col(:span="8", style="text-align: right")
      el-radio-group(:value="benefit.active ? 'ACTIVE' : 'INACTIVE'", @input="($event) => benefit.active = $event == 'ACTIVE'")
        el-radio-button.green(key="ACTIVE", label="ACTIVE") Ativo
        el-radio-button.grey(key="INACTIVE", label="INACTIVE") Inativo

      div(style="display: inline-block; width: 10px;")

      el-dropdown(split-button, @click="save(false)", @command="save(true)", type="primary", :loading="saving") Salvar
        el-dropdown-menu(slot="dropdown")
          el-dropdown-item Salvar e criar outro

  el-form(ref="form", :model="benefit", :rules="rules" label-width="180px")

    el-form-item(label="Título", prop="title")
      el-input(v-model="benefit.title")

    el-form-item(label="Descrição", prop="description")
      HtmlEditor(v-model="benefit.description", :options="descriptionEditorOptions")

    el-form-item(label="Link", prop="link")
      el-input(v-model="benefit.link")

    el-form-item(label="Imagem", prop="imageUrl")
      MediaInput(
        :filePrefix="thumbnailPrefix",
        :thumbnailUrl="benefit.imageUrl",
        :beforeUpload="beforeImageUpload",
        @update:media="onImageChange",
        :clearable="false",
      )

    el-form-item(label="Prioridade na ordenação", prop="priority")
      el-select(v-model="benefit.priority", placeholder="Selecione uma prioridade")
        el-option(label="Baixa", :value="0")
        el-option(label="Normal", :value="50")
        el-option(label="Alta", :value="100")

    el-form-item(label="Expira em", prop="expiresAt")
      el-date-picker(
        v-model="benefit.expiresAt",
        :clearable="true",
        type='date',
        placeholder='Selecione uma data',
        format='dd/MM/yyyy',
        value-format='yyyy-MM-dd'
      )
</template>
<script setup>
import HtmlEditor from '@/components/input/HtmlEditor.vue';
import MediaInput from '@/components/input/media/MediaInput.vue';
import { onMounted, ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue2-helpers/vue-router';
import { useMessage } from '@/utils/Message';
import BenefitService from '@/services/promotions/BenefitService';

const STORAGE_PREFIX = process.env.VUE_APP_MEDIA_STORAGE_PREFIX;

const route = useRoute();
const router = useRouter();
const message = useMessage();
const benefit = ref({});
const loading = ref(false);
const saving = ref(false);
const benefitId = computed(() => route.params["id"]);
const isNew = computed(() => !benefitId.value);
const form = ref();

const rules = {
  title: [{ required: true, trigger: 'blur', message: "Título obrigatório" }],
  description: [{ required: true, trigger: 'blur', message: "Descrição obrigatória" }],
  imageUrl: [{ required: true, trigger: 'blur', message: "Imagem obrigatória" }],
  priority: [{ required: true, trigger: 'blur', message: "Prioridade obrigatória" }],
  link: [{ type: 'url', trigger: 'blur', message: "Link inválido" }],
};

const thumbnailPrefix = ref("benefits/")

const descriptionEditorOptions = ref({
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['clean']
    ]
  }
})

const loadBenefit = async () => {
  if (!benefitId.value) {
    benefit.value = {
      title: "",
      description: "",
      link: "",
      active: true,
      imageUrl: null,
      priority: 0,
      expiresAt: null,
    };
    return;
  }

  loading.value = true;

  try {
    benefit.value = (await BenefitService.find(benefitId.value)) ?? {};

    benefit.value.priority = Math.floor(benefit.value.priority / 50) * 50;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};

const beforeImageUpload = async (file) => {
  const { width, height } = await (new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      resolve({
        width: image.width,
        height: image.height
      });
      URL.revokeObjectURL(image.src);
    };
    image.src = URL.createObjectURL(file);
  }));

  if (width < 720 && height < 720) {
    message.error(`Imagem deve ter pelo menos 720px de altura ou largura`);
    throw new Error(`Imagem deve ter pelo menos 720px de altura ou largura`);
  }

  return true;
};

const onImageChange = (media) => {
  benefit.value.imageUrl = `https://images.boxmagenta.com.br/fit-in/720x720/filters:quality(75)/filters:background_color(fff)/filters:format(jpeg)/${STORAGE_PREFIX + media.fileName}`
};

const save = async (createOther = false) => {
  try {
    await form.value.validate();
  } catch (e) {
    message.warning("Verifique os dados");
    console.error(e);
    return;
  }

  saving.value = true;

  try {
    const request = {
      title: benefit.value.title,
      description: benefit.value.description,
      link: benefit.value.link,
      imageUrl: benefit.value.imageUrl,
      priority: benefit.value.priority,
      active: benefit.value.active,
      expiresAt: benefit.value.expiresAt
    };

    if (isNew.value) {
      benefit.value = await BenefitService.create(request);

      router.replace({ name: "benefit-detail", params: { id: benefit.value.id } });
      if (createOther) {
        router.push({ name: "benefit-new" });
        loadBenefit();
      }
    } else {
      benefit.value = await BenefitService.update(benefitId.value, request);

      if (createOther) {
        router.push({ name: "benefit-new" });
      }
    }

    message.success(`Benefício salvo com sucesso`);
  } catch (e) {
    message.error(`Falha ao salvar: ${e}`);
  } finally {
    saving.value = false;
  }
};

const back = () => {
  router.push({ name: "benefit-list" });
};

onMounted(async () => {
  loadBenefit();
});

watch(() => route.params.id, loadBenefit);
</script>
